import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Crown, CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", plan: null, label: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "complete") {
          setState({ status: "paid", plan: data.plan, label: data.label });
          return;
        }
      } catch { /* transient poll error — retry silently; no PII/tokens logged */ }
      if (attempts >= 6) { setState({ status: "pending" }); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-primary text-primary-foreground flex items-center justify-center grain p-6">
      <div className="text-center max-w-lg relative z-10" data-testid="payment-result">
        <Link to="/" className="inline-flex items-center gap-2 mb-8"><Crown className="w-6 h-6 text-accent" /><span className="font-display font-extrabold tracking-tight">GALAHADJI</span></Link>
        {state.status === "checking" && (<>
          <Loader2 className="w-14 h-14 text-accent mx-auto animate-spin" />
          <h1 className="font-marketing text-3xl font-bold mt-6">Confirming your subscription…</h1>
          <p className="text-white/60 mt-3">One moment while we verify your payment with Stripe.</p>
        </>)}
        {state.status === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 text-accent mx-auto" data-testid="payment-success-icon" />
          <h1 className="font-marketing text-4xl font-bold mt-6">You're all set.</h1>
          <p className="text-white/70 mt-4">Your <span className="text-accent font-semibold">{state.label || "subscription"}</span> is active. Every feature on this plan is now unlocked for your team.</p>
          <Link to="/dashboard" className="inline-block mt-8 bg-accent text-accent-foreground px-7 py-3.5 rounded-md font-semibold hover:bg-accent/90 transition-colors" data-testid="go-to-dashboard">Go to Dashboard</Link>
        </>)}
        {state.status === "pending" && (<>
          <Loader2 className="w-14 h-14 text-accent mx-auto" />
          <h1 className="font-marketing text-3xl font-bold mt-6">Payment processing.</h1>
          <p className="text-white/60 mt-3">Your payment is still processing. Your plan will activate automatically once it clears.</p>
          <Link to="/dashboard" className="inline-block mt-8 bg-accent text-accent-foreground px-7 py-3.5 rounded-md font-semibold">Continue</Link>
        </>)}
        {state.status === "error" && (<>
          <XCircle className="w-16 h-16 text-white/40 mx-auto" />
          <h1 className="font-marketing text-3xl font-bold mt-6">No session found.</h1>
          <Link to="/pricing" className="inline-block mt-8 bg-accent text-accent-foreground px-7 py-3.5 rounded-md font-semibold">Back to Pricing</Link>
        </>)}
      </div>
    </div>
  );
}
